/**
 * TUVI - Gestión de Planos de Proyecto
 * CRUD para administrar planos con links de Google Drive
 * Usa paginación + cola secuencial de imágenes para evitar WAF 403
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import { BACKEND_URL } from '../../config';
import './Planos.css';

// Convierte link de Google Drive de formato compartido a formato directo de imagen
const convertirLinkDrive = (url) => {
  if (!url) return url;
  const match = url.match(/\/file\/d\/([^/?]+)/);
  if (match) return `https://drive.google.com/uc?export=view&id=${match[1]}`;
  return url;
};

// ============================================================
// Cola secuencial de imágenes: carga de 1 en 1 con delay
// Incluye cooldown post-clear para evitar WAF 403
// ============================================================
const imageQueue = {
  queue: [],
  running: false,
  delay: 500,           // ms entre cada imagen
  cooldown: 1500,       // ms de pausa después de limpiar cola (cambio de página)
  _cooldownUntil: 0,    // timestamp hasta cuando esperar

  add(callback) {
    this.queue.push(callback);
    if (!this.running) this.process();
  },

  async process() {
    this.running = true;
    // Esperar cooldown si aplica (después de cambio de página)
    const now = Date.now();
    if (this._cooldownUntil > now) {
      await new Promise(r => setTimeout(r, this._cooldownUntil - now));
    }
    while (this.queue.length > 0) {
      const cb = this.queue.shift();
      cb();
      await new Promise(r => setTimeout(r, this.delay));
    }
    this.running = false;
  },

  clear() {
    this.queue = [];
    this._cooldownUntil = Date.now() + this.cooldown;
  }
};

// ============================================================
// Componente de imagen con lazy loading + cola + reintentos
// ============================================================
const RETRY_DELAYS = [2000, 4000, 8000]; // Backoff exponencial para reintentos

const LazyImage = ({ src, alt, onClick }) => {
  const imgRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [imgSrc, setImgSrc] = useState(src);
  const retryTimerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          imageQueue.add(() => setShouldLoad(true));
          observer.disconnect();
        }
      },
      { rootMargin: '50px' }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      observer.disconnect();
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, []);

  const handleError = () => {
    if (retryCount < RETRY_DELAYS.length) {
      // Reintentar con backoff exponencial
      const delay = RETRY_DELAYS[retryCount];
      retryTimerRef.current = setTimeout(() => {
        setRetryCount(prev => prev + 1);
        // Forzar recarga agregando timestamp para evitar cache del 403
        setImgSrc(src + (src.includes('?') ? '&' : '?') + '_r=' + Date.now());
      }, delay);
    } else {
      setHasError(true);
    }
  };

  return (
    <div ref={imgRef} className={`plano-card-img ${hasError ? 'img-error' : ''}`} onClick={onClick}>
      {shouldLoad && !hasError && (
        <img
          src={imgSrc}
          alt={alt}
          style={{ opacity: loaded ? 1 : 0, transition: 'opacity 0.3s' }}
          onLoad={() => setLoaded(true)}
          onError={handleError}
        />
      )}
      {shouldLoad && !loaded && !hasError && (
        <div className="img-loading">{retryCount > 0 ? `Reintentando (${retryCount}/3)...` : 'Cargando...'}</div>
      )}
      {!shouldLoad && !hasError && (
        <div className="img-loading">...</div>
      )}
      <div className="plano-card-overlay">
        <span>Ver imagen</span>
      </div>
    </div>
  );
};

const ITEMS_PER_PAGE = 12;

const Planos = () => {
  const { success, error } = useNotification();

  const [vista, setVista] = useState('lista');
  const [planos, setPlanos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [planoEditando, setPlanoEditando] = useState(null);

  // Paginación cliente
  const [page, setPage] = useState(1);

  // Búsqueda: separar input del query real (debounce)
  const [buscarInput, setBuscarInput] = useState('');
  const [buscarQuery, setBuscarQuery] = useState('');
  const [filtroActivo, setFiltroActivo] = useState('');
  const debounceRef = useRef(null);

  // Preview de imagen
  const [previewPlano, setPreviewPlano] = useState(null);

  const [formData, setFormData] = useState({
    nombre: '',
    url_imagen: '',
    activo: 1,
    orden: ''
  });

  // ========================
  // DEBOUNCE DEL BUSCADOR
  // ========================
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setBuscarQuery(buscarInput);
    }, 800);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [buscarInput]);

  // ========================
  // CARGAR PLANOS (via POST para evitar WAF)
  // ========================
  const cargarPlanos = useCallback(async () => {
    setLoading(true);
    imageQueue.clear(); // Limpiar cola de imágenes pendientes
    try {
      const data = await client.query(`{ planos { id nombre url_imagen activo orden } }`);
      let result = data.planos || [];
      // Filtrar localmente
      if (buscarQuery.trim()) {
        const q = buscarQuery.trim().toLowerCase();
        result = result.filter(p => p.nombre.toLowerCase().includes(q));
      }
      if (filtroActivo !== '') {
        result = result.filter(p => Number(p.activo) === Number(filtroActivo));
      }
      setPlanos(result);
      setPage(1);
    } catch (err) {
      console.error('Error:', err);
      error('Error de conexión al cargar planos');
    } finally {
      setLoading(false);
    }
  }, [buscarQuery, filtroActivo, error]);

  useEffect(() => {
    cargarPlanos();
  }, [cargarPlanos]);

  // Limpiar cola al cambiar de página
  useEffect(() => {
    imageQueue.clear();
  }, [page]);

  // Paginación calculada
  const totalPages = Math.ceil(planos.length / ITEMS_PER_PAGE);
  const planosPaginados = planos.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  // ========================
  // FORMULARIO
  // ========================
  const resetForm = () => {
    setFormData({ nombre: '', url_imagen: '', activo: 1, orden: '' });
    setPlanoEditando(null);
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    const finalValue = name === 'url_imagen'
      ? convertirLinkDrive(value)
      : type === 'number' ? (value === '' ? '' : Number(value)) : value;
    setFormData(prev => ({ ...prev, [name]: finalValue }));
  };

  const nuevoPLano = () => {
    resetForm();
    setVista('crear');
  };

  const editarPlano = (plano) => {
    setPlanoEditando(plano);
    setFormData({
      nombre: plano.nombre,
      url_imagen: plano.url_imagen,
      activo: Number(plano.activo),
      orden: plano.orden || ''
    });
    setVista('editar');
  };

  const guardarPlano = async (e) => {
    e.preventDefault();

    if (!formData.nombre.trim()) {
      error('El nombre es obligatorio');
      return;
    }
    // Permitir guardar sin URL si hay archivo para subir
    if (!formData.url_imagen.trim() && !formData._archivo) {
      error('Selecciona una imagen o ingresa una URL');
      return;
    }

    setLoading(true);
    try {
      let urlImagen = formData.url_imagen;

      // Si hay archivo seleccionado, subirlo a MinIO
      if (formData._archivo) {
        const fd = new FormData();
        fd.append('archivo', formData._archivo);
        fd.append('nombre', formData.nombre.trim());
        const token = localStorage.getItem('tuvi_token');
        const uploadResp = await fetch('https://api.tuvisistema.com/upload/plano', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: fd,
        });
        const uploadData = await uploadResp.json();
        if (uploadData.success) {
          urlImagen = uploadData.minio_path;
        } else {
          error('Error al subir imagen: ' + uploadData.message);
          setLoading(false);
          return;
        }
      }

      const isEdit = vista === 'editar' && planoEditando;
      const input = { nombre: formData.nombre, url_imagen: urlImagen, activo: Number(formData.activo) === 1, orden: formData.orden ? parseInt(formData.orden) : 0 };

      let data;
      if (isEdit) {
        data = await client.mutate(`mutation($id: Int!, $input: PlanoInput!) { actualizarPlano(id: $id, input: $input) { success message } }`, { id: planoEditando.id, input });
        data = data.actualizarPlano;
      } else {
        data = await client.mutate(`mutation($input: PlanoInput!) { crearPlano(input: $input) { success message } }`, { input });
        data = data.crearPlano;
      }

      if (data.success) {
        success(data.message);
        setVista('lista');
        resetForm();
        cargarPlanos();
      } else {
        error(data.message || 'Error al guardar');
      }
    } catch (err) {
      console.error('Error:', err);
      error('Error de conexión al guardar');
    } finally {
      setLoading(false);
    }
  };

  // ========================
  // ELIMINAR
  // ========================
  const eliminarPlano = async (plano) => {
    if (!window.confirm(`¿Está seguro de eliminar el plano "${plano.nombre}"?`)) return;

    setLoading(true);
    try {
      const gqlData = await client.mutate(`mutation($id: Int!) { eliminarPlano(id: $id) { success message } }`, { id: plano.id });

      if (gqlData.eliminarPlano.success) {
        success(gqlData.eliminarPlano.message);
        cargarPlanos();
      } else {
        error(gqlData.eliminarPlano.message || 'Error al eliminar');
      }
    } catch (err) {
      console.error('Error:', err);
      error('Error de conexión al eliminar');
    } finally {
      setLoading(false);
    }
  };

  // ========================
  // TOGGLE ACTIVO
  // ========================
  const toggleActivo = async (plano) => {
    try {
      const newActivo = Number(plano.activo) !== 1;
      const gqlData = await client.mutate(`mutation($id: Int!, $input: PlanoInput!) { actualizarPlano(id: $id, input: $input) { success message } }`, {
        id: plano.id, input: { nombre: plano.nombre, url_imagen: plano.url_imagen, activo: newActivo, orden: plano.orden || 0 }
      });
      if (gqlData.actualizarPlano.success) {
        success(`Plano ${!newActivo ? 'desactivado' : 'activado'}`);
        cargarPlanos();
      } else {
        error(gqlData.actualizarPlano.message);
      }
    } catch (err) {
      error('Error al cambiar estado');
    }
  };

  // ========================
  // PREVIEW IMAGEN
  // ========================
  const getProxyUrl = (plano) => {
    const url = plano.url_imagen || '';
    // Si es MinIO, servir desde el endpoint del VPS
    if (url.startsWith('minio:')) {
      const key = url.replace('minio:planos/', '');
      return `https://api.tuvisistema.com/upload/plano-img/${encodeURIComponent(key)}`;
    }
    // Legacy: Google Drive proxy
    return `${BACKEND_URL}/api/documentos/proxy-imagen.php?plano_id=${plano.id}&url=${encodeURIComponent(url)}`;
  };

  // ========================
  // RENDER LISTA
  // ========================
  const renderLista = () => (
    <>
      <div className="planos-header">
        <div className="planos-header-info">
          <h1>Planos de Proyecto</h1>
          <p>{planos.length} planos registrados</p>
        </div>
        <div className="planos-header-actions">
          <button className="btn-nuevo" onClick={nuevoPLano}>+ Nuevo Plano</button>
        </div>
      </div>

      {/* Filtros */}
      <div className="planos-filtros">
        <div className="filtro-grupo">
          <input
            type="text"
            placeholder="Buscar por nombre... (espere a terminar de escribir)"
            value={buscarInput}
            onChange={(e) => setBuscarInput(e.target.value)}
            className="filtro-input"
          />
        </div>
        <div className="filtro-grupo">
          <select
            value={filtroActivo}
            onChange={(e) => setFiltroActivo(e.target.value)}
            className="filtro-select"
          >
            <option value="">Todos los estados</option>
            <option value="1">Activos</option>
            <option value="0">Inactivos</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="planos-loading">Cargando planos...</div>
      ) : planos.length === 0 ? (
        <div className="planos-empty">No se encontraron planos</div>
      ) : (
        <>
          <div className="planos-grid">
            {planosPaginados.map(plano => (
              <div key={plano.id} className={`plano-card ${Number(plano.activo) === 0 ? 'plano-inactivo' : ''}`}>
                <LazyImage
                  src={getProxyUrl(plano)}
                  alt={plano.nombre}
                  onClick={() => setPreviewPlano(plano)}
                />
                <div className="plano-card-body">
                  <h3 className="plano-card-title">{plano.nombre}</h3>
                  <div className="plano-card-meta">
                    <span className={`plano-estado ${Number(plano.activo) === 1 ? 'estado-activo' : 'estado-inactivo'}`}>
                      {Number(plano.activo) === 1 ? 'Activo' : 'Inactivo'}
                    </span>
                    <span className="plano-orden">#{plano.orden}</span>
                  </div>
                </div>
                <div className="plano-card-actions">
                  <button className="btn-card btn-toggle" onClick={() => toggleActivo(plano)} title={Number(plano.activo) === 1 ? 'Desactivar' : 'Activar'}>
                    {Number(plano.activo) === 1 ? '○' : '●'}
                  </button>
                  <button className="btn-card btn-editar" onClick={() => editarPlano(plano)} title="Editar">
                    ✎
                  </button>
                  <button className="btn-card btn-eliminar" onClick={() => eliminarPlano(plano)} title="Eliminar">
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="planos-paginacion">
              <button
                disabled={page <= 1}
                onClick={() => { setPage(p => p - 1); window.scrollTo(0, 0); }}
              >
                ← Anterior
              </button>
              <div className="paginacion-numeros">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(num => (
                  <button
                    key={num}
                    className={`pag-num ${num === page ? 'pag-activa' : ''}`}
                    onClick={() => { setPage(num); window.scrollTo(0, 0); }}
                  >
                    {num}
                  </button>
                ))}
              </div>
              <button
                disabled={page >= totalPages}
                onClick={() => { setPage(p => p + 1); window.scrollTo(0, 0); }}
              >
                Siguiente →
              </button>
            </div>
          )}
        </>
      )}

      {/* Modal Preview */}
      {previewPlano && (
        <div className="plano-modal-overlay" onClick={() => setPreviewPlano(null)}>
          <div className="plano-modal" onClick={(e) => e.stopPropagation()}>
            <div className="plano-modal-header">
              <h3>{previewPlano.nombre}</h3>
              <button className="btn-cerrar-modal" onClick={() => setPreviewPlano(null)}>✕</button>
            </div>
            <div className="plano-modal-body">
              <img
                src={getProxyUrl(previewPlano)}
                alt={previewPlano.nombre}
              />
            </div>
            <div className="plano-modal-footer">
              <a href={previewPlano.url_imagen} target="_blank" rel="noopener noreferrer" className="btn-link-externo">
                Abrir en Google Drive
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // ========================
  // RENDER FORMULARIO
  // ========================
  const renderFormulario = () => (
    <>
      <div className="planos-header">
        <div className="planos-header-info">
          <h1>{vista === 'crear' ? 'Nuevo Plano' : 'Editar Plano'}</h1>
          <p>{vista === 'crear' ? 'Registrar un nuevo plano de proyecto' : `Editando: ${planoEditando?.nombre}`}</p>
        </div>
        <div className="planos-header-actions">
          <button className="btn-volver" onClick={() => { setVista('lista'); resetForm(); }}>Volver al listado</button>
        </div>
      </div>

      <form onSubmit={guardarPlano} className="plano-form">
        <div className="form-row">
          <div className="form-group form-group-lg">
            <label>Nombre del Plano *</label>
            <input
              type="text"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              placeholder="Ej: La Heredia Tipo 1"
              required
            />
          </div>
          <div className="form-group form-group-sm">
            <label>Orden</label>
            <input
              type="number"
              name="orden"
              value={formData.orden}
              onChange={handleChange}
              placeholder="Auto"
              min="0"
            />
          </div>
          <div className="form-group form-group-sm">
            <label>Estado</label>
            <select name="activo" value={formData.activo} onChange={handleChange}>
              <option value={1}>Activo</option>
              <option value={0}>Inactivo</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Imagen del Plano *</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) setFormData(prev => ({ ...prev, _archivo: file, url_imagen: 'pending-upload' }));
            }}
            style={{ marginBottom: '8px' }}
          />
          {formData._archivo && (
            <span className="form-help" style={{ color: '#10b981' }}>
              Archivo seleccionado: {formData._archivo.name} ({(formData._archivo.size / 1024).toFixed(0)} KB)
            </span>
          )}
          <details style={{ marginTop: '8px' }}>
            <summary style={{ cursor: 'pointer', fontSize: '12px', color: '#666' }}>O usar URL de Google Drive</summary>
            <input
              type="url"
              name="url_imagen"
              value={formData.url_imagen === 'pending-upload' ? '' : formData.url_imagen}
              onChange={handleChange}
              placeholder="https://drive.google.com/file/d/ID/view"
              style={{ marginTop: '4px' }}
            />
          </details>
        </div>

        {/* Preview de la imagen */}
        {formData.url_imagen && (
          <div className="form-preview">
            <label>Vista previa</label>
            <div className="form-preview-img">
              {vista === 'editar' && planoEditando ? (
                <img
                  src={getProxyUrl(planoEditando)}
                  alt="Preview"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : (
                <img
                  src={formData.url_imagen}
                  alt="Preview"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              )}
              <div className="preview-fallback" style={{ display: 'none' }}>
                <span>La imagen se mostrará después de guardar (requiere proxy)</span>
              </div>
            </div>
          </div>
        )}

        <div className="form-actions">
          <button type="button" className="btn-cancelar" onClick={() => { setVista('lista'); resetForm(); }}>
            Cancelar
          </button>
          <button type="submit" className="btn-guardar" disabled={loading}>
            {loading ? 'Guardando...' : (vista === 'crear' ? 'Crear Plano' : 'Guardar Cambios')}
          </button>
        </div>
      </form>
    </>
  );

  // ========================
  // RENDER PRINCIPAL
  // ========================
  return (
    <div className="planos-container">
      {vista === 'lista' ? renderLista() : renderFormulario()}
    </div>
  );
};

export default Planos;
